'use client';

import { Button } from '@workspace/ui/components/button';
import { TruncateWithTooltip } from '@workspace/ui/components/custom/truncate-with-tooltip';
import { cn } from '@workspace/ui/lib';
import { ChevronDown, ChevronUp, Download, Eye, FileText, Upload, X } from 'lucide-react';
import Image from 'next/image';
import type React from 'react';
import { useRef, useState } from 'react';
// import { useTranslations } from "next-intl";

/** Display-only entry for name-only rows (not a browser `File`). */
export type FileInputListItem = File | { placeholderName: string };

function fileInputItemName(item: FileInputListItem): string {
  return item instanceof File ? item.name : item.placeholderName;
}

function fileInputItemSize(item: FileInputListItem): number {
  return item instanceof File ? item.size : 0;
}

function fileInputItemLastModified(item: FileInputListItem): number {
  return item instanceof File ? item.lastModified : 0;
}

function fileInputItemType(item: FileInputListItem): string {
  return item instanceof File ? item.type : '';
}

/** Lets existing `(files: File | File[]) => void` handlers stay compatible with placeholder list items. */
export type FileInputOnChange = {
  bivarianceHack(files: File | File[] | FileInputListItem[]): void;
}['bivarianceHack'];

export interface FileInputProps {
  files: FileInputListItem[] | null;
  onChange: FileInputOnChange;
  multiple?: boolean;
  /**
   * Comma-separated MIME types and/or file extensions to allow (same tokens as the native `accept` attribute, plus plain extensions like `xlsx`).
   * When omitted or empty, any file type is allowed (no `accept` filter, no type validation on drop/select).
   */
  allowedDocumentTypes?: string;
  /** Text before the inline file-picker control (default: dropzone copy). */
  description?: string;
  descriptionAfter?: string;
  subDescription?: React.ReactNode;
  /**
   * Overrides the default “Accepted formats: …” line derived from `allowedDocumentTypes`.
   * When omitted, formats are listed automatically (hidden when `allowedDocumentTypes` is `*`).
   */
  acceptedFormatsLabel?: string;
  /**
   * Max upload size in bytes. When omitted, defaults to 20 MB (enforced and shown in helper text
   * unless you override with `maxFileSizeLabel`).
   */
  maxFileSizeBytes?: number;
  /** e.g. “Upload files” above the field */
  label?: React.ReactNode;
  /** Replaces the auto “N files uploaded” line when multiple files are shown. */
  uploadedFilesCaption?: React.ReactNode;
  showPreviewIcon?: boolean;
  showDownloadIcon?: boolean;
  onPreviewFile?: (file: File) => void;
  onDownloadFile?: (file: File) => void;
  className?: string;
  onBlur?: () => void;
  'aria-invalid'?: boolean;
  readOnly?: boolean;
  selectButtonVariant?: React.ComponentProps<typeof Button>['variant'];
  selectButtonText?: string;
  showDeleteButton?: boolean;
  customFileTypeValidation?: string;
  /** Renders under the field and contributes to the error border (e.g. from form validation). */
  errorText?: string;
  /** Primary line in the empty dropzone on small screens (default: “Take a photo or select files”). */
  mobileEmptyPrimaryText?: string;
}

type SelectButtonVariant = React.ComponentProps<typeof Button>['variant'];

// --- helpers -----------------------------------------------------------------

/** Default `maxFileSizeBytes` when the prop is not passed. */
const DEFAULT_MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
  const value = bytes / k ** i;
  const decimals = i > 0 ? 1 : 0;
  return `${parseFloat(value.toFixed(decimals))} ${sizes[i]}`;
}

function defaultUploadedCaption(count: number): string {
  return count === 1 ? '1 file uploaded' : `${count} files uploaded`;
}

function selectLinkClassName(variant: SelectButtonVariant): string {
  switch (variant) {
    case 'primary':
    case 'secondary':
      return 'text-primary dark:text-neutral-100';
    case 'pink':
    case 'pink-outline':
    case 'pink-ghost':
      return 'text-pink-400 dark:text-pink-300';
    case 'destructive':
      return 'text-destructive dark:text-red-400';
    default:
      return 'text-grey-900 dark:text-grey-100';
  }
}

function fileMatchesType(file: File, type: string): boolean {
  const t = type.trim();
  if (!t) return false;
  if (t.endsWith('/*')) {
    const category = t.split('/')[0];
    return file.type.startsWith(`${category}/`);
  }
  if (file.type === t) return true;
  if (t.startsWith('.') && file.name.toLowerCase().endsWith(t.toLowerCase())) return true;
  if (
    !t.startsWith('.') &&
    !t.includes('/') &&
    file.name.toLowerCase().endsWith(`.${t.toLowerCase()}`)
  ) {
    return true;
  }
  return false;
}

function isAcceptAllDocumentTypes(allowedDocumentTypes: string): boolean {
  const t = allowedDocumentTypes.trim();
  return t === '' || t === '*';
}

/** Extensions commonly produced by cameras or treated as photos in `accept` / validation. */
const PHOTO_FILE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp']);

function tokenAllowsPhotoCapture(token: string): boolean {
  const p = token.trim();
  if (!p) return false;
  if (p === 'image/*' || (p.endsWith('/*') && p.slice(0, -2) === 'image')) return true;
  if (p.startsWith('image/')) return true;
  if (p.startsWith('.')) {
    return PHOTO_FILE_EXTENSIONS.has(p.slice(1).toLowerCase());
  }
  if (!p.includes('/')) {
    return PHOTO_FILE_EXTENSIONS.has(p.toLowerCase());
  }
  return false;
}

/** True when at least one allowed token permits a still image suitable for mobile camera capture. */
function allowedTypesAllowPhotoCapture(allowedDocumentTypes: string): boolean {
  if (isAcceptAllDocumentTypes(allowedDocumentTypes)) return true;
  const parts = allowedDocumentTypes
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
  return parts.some(tokenAllowsPhotoCapture);
}

/** `undefined` / empty / `*` → internal sentinel meaning “all types”. */
function normalizeAllowedDocumentTypes(allowed: string | undefined): string {
  if (allowed == null) return '*';
  const t = allowed.trim();
  if (t === '') return '*';
  return t;
}

function filterFilesByAllowedTypes(newFiles: File[], allowedDocumentTypes: string): File[] {
  if (isAcceptAllDocumentTypes(allowedDocumentTypes)) return newFiles;
  const allowedTypes = allowedDocumentTypes
    .split(',')
    .map((p) => p.trim())
    // drop empty segments after splitting and trimming
    .filter(Boolean);
  return newFiles.filter((file) => allowedTypes.some((type) => fileMatchesType(file, type)));
}

function buildAcceptAttribute(allowedDocumentTypes: string): string | undefined {
  if (isAcceptAllDocumentTypes(allowedDocumentTypes)) {
    return undefined;
  }
  if (allowedDocumentTypes.includes('xlsx') || allowedDocumentTypes.includes('xls')) {
    return `${allowedDocumentTypes},.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xls,application/vnd.ms-excel`;
  }
  return allowedDocumentTypes;
}

const MIME_TYPE_TO_FORMAT_LABEL: Record<string, string> = {
  'text/csv': 'CSV',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.ms-excel': 'XLS',
  'application/pdf': 'PDF',
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/webp': 'WEBP',
  'image/gif': 'GIF',
};

const EXT_TO_FORMAT_LABEL: Record<string, string> = {
  csv: 'CSV',
  xlsx: 'XLSX',
  xls: 'XLS',
  pdf: 'PDF',
  png: 'PNG',
  jpg: 'JPG',
  jpeg: 'JPEG',
  webp: 'WEBP',
  gif: 'GIF',
  doc: 'DOC',
  docx: 'DOCX',
};

function formatLabelForExtensionOrBareToken(token: string): string {
  const ext = token.replace(/^\./, '').toLowerCase();
  return EXT_TO_FORMAT_LABEL[ext] ?? ext.toUpperCase();
}

/** Fallback when a MIME is not in `MIME_TYPE_TO_FORMAT_LABEL` (e.g. custom vendor types). */
function formatLabelForUnknownMimeType(mime: string): string {
  const sub = mime.split('/')[1];
  if (!sub) return mime.toUpperCase();
  const lastSegment = sub.split(/[./+]/).filter(Boolean).pop() ?? sub;
  if (lastSegment.length > 0 && lastSegment.length <= 12) {
    return lastSegment.toUpperCase();
  }
  return sub.length <= 16 ? sub.toUpperCase() : `${sub.slice(0, 14).toUpperCase()}…`;
}

/** Returns a line like “Accepted formats: CSV, XLSX”, or null when not applicable. */
function defaultAcceptedFormatsLine(allowedDocumentTypes: string): string | null {
  const t = allowedDocumentTypes.trim();
  if (!t || t === '*') return null;
  if (t.endsWith('/*')) {
    const category = t.slice(0, -2);
    if (category === 'image') return 'Accepted formats: Images';
    if (category === 'video') return 'Accepted formats: Videos';
    if (category === 'audio') return 'Accepted formats: Audio';
    const name = category ? `${category.charAt(0).toUpperCase()}${category.slice(1)}` : 'File';
    return `Accepted formats: ${name} files`;
  }
  const parts = t
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const p of parts) {
    let label: string | null = null;
    if (p.startsWith('.')) {
      label = formatLabelForExtensionOrBareToken(p);
    } else if (p.includes('/')) {
      label = MIME_TYPE_TO_FORMAT_LABEL[p] ?? formatLabelForUnknownMimeType(p);
    } else {
      label = formatLabelForExtensionOrBareToken(p);
    }
    if (label && !seen.has(label)) {
      seen.add(label);
      labels.push(label);
    }
  }
  if (labels.length === 0) return null;
  return `Accepted formats: ${labels.join(', ')}`;
}

// --- class fragments (reused) ------------------------------------------------

const FILE_ITEM_ACTION_CLASS = cn(
  'text-grey-800 hover:text-grey-1000 dark:text-grey-200 dark:hover:text-neutral-100',
  'size-4 !h-4 !w-4 !min-h-0 !min-w-0 !max-h-4 !max-w-4 !p-0 !py-0',
  'shrink-0 rounded-sm [&_svg]:!h-4 [&_svg]:!w-4 [&_svg]:shrink-0'
);
const FILE_ITEM_ACTION_ICON_CLASS = 'h-4 w-4 text-grey-800 stroke-2! dark:text-grey-200';
const FILE_NAME_READ_ONLY = 'text-base font-normal leading-4 text-neutral-200 dark:text-grey-100';
const FILE_NAME_EDITABLE = 'text-sm font-medium text-grey-800 dark:text-grey-200 leading-tight';

// --- subcomponents (internal) ------------------------------------------------

function FileListActionButtons(props: {
  file: File;
  readOnly: boolean;
  showDeleteButton: boolean;
  showDownloadIcon: boolean;
  showPreviewIcon: boolean;
  onDownloadFile?: (file: File) => void;
  onPreviewFile?: (file: File) => void;
  /** Omit in read-only rows that only show download/preview. */
  onRemove?: () => void;
}) {
  const {
    file,
    readOnly,
    showDeleteButton,
    showDownloadIcon,
    showPreviewIcon,
    onDownloadFile,
    onPreviewFile,
    onRemove,
  } = props;

  return (
    <div className="z-10 flex shrink-0 items-start justify-end gap-2">
      {showDownloadIcon && onDownloadFile ? (
        <Button
          variant="ghost"
          size="sm"
          className={FILE_ITEM_ACTION_CLASS}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            onDownloadFile(file);
          }}
          type="button"
          aria-label={`Download ${file.name}`}
        >
          <Download className={FILE_ITEM_ACTION_ICON_CLASS} />
        </Button>
      ) : null}
      {showPreviewIcon && onPreviewFile ? (
        <Button
          variant="ghost"
          size="sm"
          className={FILE_ITEM_ACTION_CLASS}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            onPreviewFile(file);
          }}
          type="button"
          aria-label={`Preview ${file.name}`}
        >
          <Eye className={FILE_ITEM_ACTION_ICON_CLASS} />
        </Button>
      ) : null}
      {!readOnly && showDeleteButton && onRemove ? (
        <Button
          variant="ghost"
          size="sm"
          className={FILE_ITEM_ACTION_CLASS}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
          type="button"
          aria-label={`Remove ${file.name}`}
        >
          <X className={FILE_ITEM_ACTION_ICON_CLASS} />
        </Button>
      ) : null}
    </div>
  );
}

function FileListFileIcon() {
  return (
    <FileText className="text-red-400 dark:text-red-300 h-4 w-4 " strokeWidth={2} aria-hidden />
  );
}

function MultipleFileListItem(props: {
  file: File;
  readOnly: boolean;
  needsFileListScroll: boolean;
  showDeleteButton: boolean;
  showDownloadIcon: boolean;
  showPreviewIcon: boolean;
  onRemove: () => void;
  onDownloadFile?: (file: File) => void;
  onPreviewFile?: (file: File) => void;
}) {
  const {
    file,
    readOnly,
    needsFileListScroll,
    showDeleteButton,
    showDownloadIcon,
    showPreviewIcon,
    onRemove,
    onDownloadFile,
    onPreviewFile,
  } = props;

  return (
    <li
      className={cn(
        readOnly ? 'p-4' : 'p-2',
        readOnly &&
          'rounded-lg border border-grey-200 dark:border-grey-700 dark:bg-grey-1000 h-13 ',
        needsFileListScroll ? (readOnly ? 'min-h-13' : 'min-h-17') : ''
      )}
    >
      <div
        className={cn(
          'flex w-full min-w-0 items-start gap-2 self-stretch',
          readOnly ? 'h-9' : 'min-h-8'
        )}
      >
        <FileListFileIcon />
        <div
          className={cn(
            'inline-flex min-w-0 flex-1 flex-col items-start self-stretch',
            readOnly ? 'justify-start' : 'justify-between'
          )}
        >
          <TruncateWithTooltip
            className={cn('w-full', readOnly ? FILE_NAME_READ_ONLY : FILE_NAME_EDITABLE)}
          >
            {file.name}
          </TruncateWithTooltip>
          {!readOnly ? (
            <div className="text-grey-500 dark:text-grey-400 w-full text-xs leading-4 font-normal">
              {formatFileSize(file.size)}
            </div>
          ) : null}
        </div>

        <FileListActionButtons
          file={file}
          readOnly={readOnly}
          showDeleteButton={showDeleteButton}
          showDownloadIcon={showDownloadIcon}
          showPreviewIcon={showPreviewIcon}
          onDownloadFile={onDownloadFile}
          onPreviewFile={onPreviewFile}
          onRemove={onRemove}
        />
      </div>
    </li>
  );
}

function ReplaceAndDeleteFileControls(props: {
  fileInputEl: React.ReactNode;
  onClear: () => void;
  onReplace: (e: React.MouseEvent) => void;
  showDeleteButton: boolean;
}) {
  const { fileInputEl, onClear, onReplace, showDeleteButton } = props;
  return (
    <>
      <Button
        variant="secondary"
        className="w-full max-w-[140px]"
        onClick={onReplace}
        type="button"
        aria-label="Replace current file"
      >
        Replace
      </Button>
      {showDeleteButton ? (
        <Button
          variant="destructive"
          className="w-full max-w-[140px]"
          onClick={onClear}
          type="button"
          aria-label="Delete current file"
        >
          Delete
        </Button>
      ) : null}
      {fileInputEl}
    </>
  );
}

function ReadOnlySingleFileCard(props: {
  label: React.ReactNode | undefined;
  file: File;
  showDownloadIcon: boolean;
  showPreviewIcon: boolean;
  onDownloadFile?: (file: File) => void;
  onPreviewFile?: (file: File) => void;
  cardClassName: string;
}) {
  const {
    label,
    file,
    showDownloadIcon,
    showPreviewIcon,
    onDownloadFile,
    onPreviewFile,
    cardClassName,
  } = props;
  return (
    <div className={cardClassName}>
      {label ? (
        <>
          <div className="px-4 pt-4 pb-3">
            <p className="text-grey-900 dark:text-grey-100 m-0 text-sm font-semibold">{label}</p>
          </div>
          <div className="border-grey-200 dark:border-grey-700 border-b" />
        </>
      ) : null}
      <div className="px-4 pt-4 pb-4">
        <div className="rounded-lg border border-grey-200 bg-white p-4 dark:border-grey-600 dark:bg-grey-800">
          <div className="flex h-9 w-full min-w-0 items-start gap-2 self-stretch">
            <FileListFileIcon />
            <div className="inline-flex min-w-0 flex-1 flex-col items-start justify-start self-stretch">
              <TruncateWithTooltip className={cn('w-full', FILE_NAME_READ_ONLY)}>
                {file.name}
              </TruncateWithTooltip>
            </div>
            <FileListActionButtons
              file={file}
              readOnly
              showDeleteButton={false}
              showDownloadIcon={showDownloadIcon}
              showPreviewIcon={showPreviewIcon}
              onDownloadFile={onDownloadFile}
              onPreviewFile={onPreviewFile}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// --- main component ----------------------------------------------------------

export function FileInput({
  files,
  onChange,
  multiple = false,
  allowedDocumentTypes,
  description = 'Drop your files here or',
  descriptionAfter,
  subDescription,
  acceptedFormatsLabel,
  maxFileSizeBytes,
  label,
  uploadedFilesCaption,
  showPreviewIcon = false,
  showDownloadIcon = false,
  onPreviewFile,
  onDownloadFile,
  className = '',
  onBlur,
  readOnly = false,
  selectButtonVariant = 'secondary',
  selectButtonText,
  showDeleteButton = true,
  customFileTypeValidation,
  errorText,
  ...props
}: FileInputProps) {
  const [isDragged, setIsDragged] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const [readOnlyListExpanded, setReadOnlyListExpanded] = useState(false);

  const invalid = 'aria-invalid' in props && Boolean(props['aria-invalid']);
  const displayErrorMessage = error ?? errorText ?? null;
  const hasError = invalid || (displayErrorMessage != null && displayErrorMessage.length > 0);

  const resolvedAllowedTypes = normalizeAllowedDocumentTypes(allowedDocumentTypes);
  const resolvedAcceptedFormatsLabel =
    acceptedFormatsLabel ?? defaultAcceptedFormatsLine(resolvedAllowedTypes);
  const effectiveMaxFileSizeBytes = maxFileSizeBytes ?? DEFAULT_MAX_FILE_SIZE_BYTES;
  const resolvedMaxFileSizeLabel = `Maximum size: ${formatFileSize(effectiveMaxFileSizeBytes)}`;
  const showMobileTakePhoto = allowedTypesAllowPhotoCapture(resolvedAllowedTypes);

  const handleFileChange = (newFiles: File[]) => {
    const afterType = filterFilesByAllowedTypes(newFiles, resolvedAllowedTypes);
    const afterSize = afterType.filter((file) => file.size <= effectiveMaxFileSizeBytes);
    if (afterType.length !== newFiles.length) {
      setError(customFileTypeValidation || 'Invalid file type');
    } else if (afterSize.length !== afterType.length) {
      setError(`File must be at most ${formatFileSize(effectiveMaxFileSizeBytes)}`);
    } else {
      setError(null);
    }
    if (multiple) onChange([...(files || []), ...afterSize]);
    else onChange(afterSize.slice(0, 1));
  };

  const handleRemoveFileAtIndex = (removeIndex: number) => {
    if (!files || removeIndex < 0 || removeIndex >= files.length) return;
    const newFiles = files.filter((_, index) => index !== removeIndex);
    onChange(newFiles.length > 0 ? newFiles : []);
    setInputKey((prev) => prev + 1);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragged(false);
    handleFileChange(Array.from(e.dataTransfer.files));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) handleFileChange(Array.from(e.target.files));
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const clearSelectedFile = () => {
    onChange([]);
    setInputKey((prev) => prev + 1);
  };

  const hasFiles = Boolean(files && files.length > 0);
  const fileList = files ?? [];

  const cardClassName = cn(
    'w-full overflow-hidden rounded-2xl border transition-colors',
    hasError
      ? 'border-solid border-red-500 bg-white dark:border-red-400 dark:bg-grey-1000'
      : 'border-dashed bg-neutral-100 border-grey-300 hover:border-grey-400 dark:bg-grey-1000 dark:border-grey-600 dark:hover:border-grey-500',
    isDragged && !hasError && 'border-primary dark:border-pink-400'
  );

  const errorMessageEl =
    displayErrorMessage != null && displayErrorMessage !== '' ? (
      <p className="text-red-500 dark:text-red-400 mt-2 text-left text-sm font-normal" role="alert">
        {displayErrorMessage}
      </p>
    ) : null;

  const dragHandlers = {
    onDragOver: (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragged(true);
    },
    onDragLeave: (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragged(false);
    },
    onDrop: handleDrop,
    onBlur,
  };

  const fileInputEl = (
    <input
      ref={fileInputRef}
      type="file"
      accept={buildAcceptAttribute(resolvedAllowedTypes)}
      multiple={multiple}
      className="hidden"
      aria-invalid={hasError}
      onChange={handleInputChange}
      key={inputKey}
    />
  );

  const metaLines = (
    <>
      {resolvedAcceptedFormatsLabel ? (
        <p className="text-grey-500 dark:text-grey-400 m-0 text-center text-xs">
          {resolvedAcceptedFormatsLabel}
        </p>
      ) : null}
      {resolvedMaxFileSizeLabel ? (
        <p className="text-grey-500 dark:text-grey-400 m-0 text-center text-xs">
          {resolvedMaxFileSizeLabel}
        </p>
      ) : null}
      {subDescription ? (
        <div className="text-grey-500 dark:text-grey-400 mt-1 text-center text-xs whitespace-pre-line">
          {typeof subDescription === 'string' ? <p>{subDescription}</p> : subDescription}
        </div>
      ) : null}
    </>
  );

  const primaryLineWithLink = (
    <p className="text-grey-800 dark:text-grey-200 text-center text-sm leading-relaxed">
      <span>{description}</span>{' '}
      <button
        type="button"
        className={cn(
          'cursor-pointer font-semibold underline underline-offset-2',
          selectLinkClassName(selectButtonVariant)
        )}
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          openFileDialog();
        }}
      >
        {selectButtonText ?? 'select files'}
      </button>
      <span>{descriptionAfter}</span>
    </p>
  );

  const emptyDropzoneDesktop = (
    <div
      className="hidden cursor-pointer flex-col items-center gap-4 md:flex"
      onClick={openFileDialog}
      role="presentation"
    >
      <Upload
        className="text-grey-800 dark:text-grey-100 h-6 w-6 shrink-0"
        strokeWidth={1.5}
        aria-hidden
      />
      <div className="flex flex-col items-center gap-2">
        {primaryLineWithLink}
        <div className="flex flex-col items-center gap-0">{metaLines}</div>
      </div>
    </div>
  );

  const emptyDropzoneMobile = (
    <div className="flex flex-col items-center gap-5 md:hidden">
      <Upload
        className="text-grey-600 dark:text-grey-300 h-8 w-8 shrink-0"
        strokeWidth={1.5}
        aria-hidden
      />
      <div className="flex w-full flex-col items-center gap-2">
        <p className="text-grey-800 dark:text-grey-200 m-0 text-center text-sm font-medium">
          {description}
        </p>
        <div className="flex flex-col items-center gap-1">{metaLines}</div>
      </div>
      <div className="flex w-full gap-3">
        {showMobileTakePhoto ? (
          <Button
            asChild
            variant="secondary"
            size="sm"
            className="relative h-11 min-h-11 flex-1 rounded-full font-medium"
          >
            <label>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                aria-label="Take photo"
                onChange={handleInputChange}
                key={`cam-${inputKey}`}
              />
              <span className="pointer-events-none">Take photo</span>
            </label>
          </Button>
        ) : null}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="h-11 min-h-11 flex-1 rounded-full font-medium"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openFileDialog();
          }}
        >
          {selectButtonText ?? 'Select files'}
        </Button>
      </div>
    </div>
  );

  const emptyDropzone = (
    <div className="w-full" {...dragHandlers} role="presentation">
      {emptyDropzoneMobile}
      {emptyDropzoneDesktop}
      {fileInputEl}
    </div>
  );

  const needsFileListScroll = fileList.length > 4;
  const readOnlyManyFiles = readOnly && fileList.length > 4;
  const showReadOnlyFileList = !readOnlyManyFiles || readOnlyListExpanded;

  const multipleFileList = (
    <ul className={cn('m-0 flex list-none flex-col p-0', readOnly ? 'gap-3' : 'gap-2')}>
      {fileList.map((file, index) => (
        <MultipleFileListItem
          key={`${index}-${inputKey}`}
          file={file as File}
          readOnly={readOnly}
          needsFileListScroll={needsFileListScroll}
          showDeleteButton={showDeleteButton}
          showDownloadIcon={showDownloadIcon}
          showPreviewIcon={showPreviewIcon}
          onRemove={() => handleRemoveFileAtIndex(index)}
          onDownloadFile={onDownloadFile}
          onPreviewFile={onPreviewFile}
        />
      ))}
    </ul>
  );

  const multipleFileListWithScroll = needsFileListScroll ? (
    <div
      className={cn(
        'overflow-y-auto overscroll-contain pl-0 pr-[12px] [scrollbar-gutter:stable]',
        readOnly ? 'max-h-[calc(4*4.25rem+3*0.75rem)]' : 'max-h-[calc(4*4.25rem+3*0.5rem)]'
      )}
    >
      {multipleFileList}
    </div>
  ) : (
    multipleFileList
  );

  const readOnlyAttachmentsCardClass = cn(
    'w-full overflow-hidden rounded-2xl border border-solid',
    hasError
      ? 'border-red-500 bg-white dark:border-red-400 dark:bg-grey-1000'
      : 'border-grey-300 bg-neutral-100 dark:border-grey-600 dark:bg-grey-1000'
  );

  const rootWidthClass = 'w-full min-w-0 max-w-full';

  const readOnlyListToggleButton = readOnlyManyFiles ? (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="text-grey-800 hover:text-grey-1000 dark:text-grey-200 dark:hover:text-grey-50 h-8 w-8 shrink-0 p-0"
      onClick={() => setReadOnlyListExpanded((open) => !open)}
      aria-expanded={readOnlyListExpanded}
      aria-label={readOnlyListExpanded ? 'Collapse attachment list' : 'Expand attachment list'}
    >
      {readOnlyListExpanded ? (
        <ChevronUp className="h-4 w-4" strokeWidth={2} />
      ) : (
        <ChevronDown className="h-4 w-4" strokeWidth={2} />
      )}
    </Button>
  ) : null;

  if (readOnly && multiple) {
    const readOnlyCaptionInMergedRow = readOnlyManyFiles && !label;
    const readOnlyCollapsedBarTitle: React.ReactNode =
      label ??
      uploadedFilesCaption ??
      (fileList.length === 1 ? '1 file' : `${fileList.length} files`);
    return (
      <div className={cn(rootWidthClass, className)}>
        {hasFiles ? (
          readOnlyManyFiles && !readOnlyListExpanded ? (
            <button
              type="button"
              className={cn(
                'flex h-[64px] w-full min-w-0 shrink-0 cursor-pointer items-center justify-between gap-2 px-4 text-left text-sm font-semibold',
                'text-grey-900 dark:text-grey-100',
                readOnlyAttachmentsCardClass,
                'hover:bg-grey-50 dark:hover:bg-grey-900/60'
              )}
              onClick={() => setReadOnlyListExpanded(true)}
              aria-expanded={false}
              aria-label="Expand attachment list"
            >
              <span className="min-w-0 flex-1 truncate text-inherit">
                {readOnlyCollapsedBarTitle}
              </span>
              <ChevronDown
                className="h-4 w-4 shrink-0 text-grey-800 dark:text-grey-200"
                strokeWidth={2}
                aria-hidden
              />
            </button>
          ) : (
            <div className={readOnlyAttachmentsCardClass}>
              {label ? (
                <>
                  <div className="flex items-center justify-between gap-2 px-6 pt-6 pb-[12px]">
                    <p className="text-grey-900 dark:text-grey-100 m-0 min-w-0 flex text-sm font-semibold">
                      {label}
                    </p>
                    {readOnlyListToggleButton}
                  </div>
                  <div
                    className="mx-6 border-b border-grey-200 dark:border-grey-700"
                    role="separator"
                  />
                </>
              ) : null}
              <div className={cn('px-6 pb-6', label ? 'pt-4' : 'pt-6')}>
                {readOnlyCaptionInMergedRow ? (
                  <div className="text-grey-500 dark:text-grey-400 mb-2 flex items-center justify-between gap-2 text-xs font-medium">
                    <div className="m-0 min-w-0 flex">
                      {uploadedFilesCaption ??
                        (fileList.length === 1 ? '1 file' : `${fileList.length} files`)}
                    </div>
                    {readOnlyListToggleButton}
                  </div>
                ) : !readOnlyManyFiles && uploadedFilesCaption ? (
                  <p className="text-grey-500 dark:text-grey-400 mb-2 text-xs font-medium">
                    {uploadedFilesCaption}
                  </p>
                ) : null}
                {readOnlyManyFiles && label && readOnlyListExpanded && uploadedFilesCaption ? (
                  <p className="text-grey-500 dark:text-grey-400 mb-2 text-xs font-medium">
                    {uploadedFilesCaption}
                  </p>
                ) : null}
                {showReadOnlyFileList ? multipleFileListWithScroll : null}
              </div>
            </div>
          )
        ) : null}
        {errorMessageEl}
      </div>
    );
  }

  if (readOnly && !multiple && hasFiles) {
    const file = fileList[0]!;
    return (
      <div className={cn(rootWidthClass, className)}>
        <ReadOnlySingleFileCard
          label={label}
          file={file as File}
          showDownloadIcon={showDownloadIcon}
          showPreviewIcon={showPreviewIcon}
          onDownloadFile={onDownloadFile}
          onPreviewFile={onPreviewFile}
          cardClassName={readOnlyAttachmentsCardClass}
        />
        {errorMessageEl}
      </div>
    );
  }

  if (readOnly && !hasFiles) {
    return <div className={cn(rootWidthClass, className)}>{errorMessageEl}</div>;
  }

  const firstFile = fileList[0];

  return (
    <div className={cn(rootWidthClass, className)}>
      {label ? (
        <p className="text-grey-900 dark:text-grey-100 mb-2 text-sm font-semibold">{label}</p>
      ) : null}

      <div className={cn(cardClassName, (multiple || !hasFiles) && 'pt-4 px-4 pb-5')}>
        {multiple ? (
          <>
            {emptyDropzone}
            {hasFiles ? (
              <>
                <div
                  className="border-grey-200 dark:border-grey-700 my-[25px] border-t"
                  role="separator"
                />
                <div>
                  <p className="text-grey-500 dark:text-grey-400 mb-2 text-xs font-normal leading-3">
                    {uploadedFilesCaption ?? defaultUploadedCaption(fileList.length)}
                  </p>
                  {multipleFileListWithScroll}
                </div>
              </>
            ) : null}
          </>
        ) : hasFiles && fileInputItemType(firstFile as FileInputListItem).startsWith('image/') ? (
          <div className="flex flex-col items-center gap-4 px-6 py-9" {...dragHandlers}>
            <Image
              width={90}
              height={90}
              className="border-grey-500 h-[90px] w-[90px] rounded-2xl border border-dashed object-contain p-1"
              src={URL.createObjectURL(firstFile as File)}
              alt="Preview"
            />
            <ReplaceAndDeleteFileControls
              fileInputEl={fileInputEl}
              showDeleteButton={showDeleteButton}
              onReplace={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                openFileDialog();
              }}
              onClear={clearSelectedFile}
            />
          </div>
        ) : hasFiles && firstFile ? (
          <div className="flex flex-col items-center gap-4 px-6 py-9" {...dragHandlers}>
            <FileText
              className="h-9 w-9 text-red-500 dark:text-red-400"
              strokeWidth={2}
              aria-hidden
            />
            <p
              className={cn('min-w-0 max-w-full wrap-break-word text-center', FILE_NAME_READ_ONLY)}
            >
              {(firstFile as File).name}
            </p>
            <p className="text-grey-500 dark:text-grey-400 text-xs">
              {formatFileSize((firstFile as File).size)}
            </p>
            <ReplaceAndDeleteFileControls
              fileInputEl={fileInputEl}
              showDeleteButton={showDeleteButton}
              onReplace={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                openFileDialog();
              }}
              onClear={clearSelectedFile}
            />
          </div>
        ) : (
          emptyDropzone
        )}
      </div>

      {errorMessageEl}
    </div>
  );
}
